import { LayoutHeader } from "@/Header";
import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { ContractPinGate } from "@/business/contract/ContractPinGate";
import type {
  ContractField,
  FieldType,
} from "@/business/contract/contractTemplate";
import {
  useCreateContractTemplate,
  useUploadImportSource,
} from "~/services/business/contractTemplate";
import type { CdFieldType, ContractFieldDto } from "~/types/common";
import { IconUpload } from "@/business/contract/icons";
import { ContractRichEditor } from "@/business/contract/ContractRichEditor";

/**
 * 계약서 양식 임포트 화면 (AI 원클릭 흐름).
 *
 * 흐름:
 *   1. PDF/DOCX 선택
 *   2. 크레딧 확인 다이얼로그
 *   3. 파일 업로드 → Gemini AI 자동 변환 (2~5초)
 *   4. 결과를 CreateContractTemplateRequest 로 즉시 저장
 *      (cdEditStep=REVIEW · 검토 화면에서 시작)
 *   5. 계약서 편집기(Step 3 검토)로 자동 이동 → 사용자는 미리보기만 확인
 */
export const Route = createFileRoute(
  "/business/layout/contract-template-import",
)({
  component: RouteComponent,
});

const FE_TO_BE_FIELD: Record<FieldType, CdFieldType> = {
  variable: "FIELD_TYPE_VAR",
  text: "FIELD_TYPE_TEXT",
  longText: "FIELD_TYPE_TEXT",
  date: "FIELD_TYPE_DATE",
  checkbox: "FIELD_TYPE_CHECKBOX",
  signature: "FIELD_TYPE_SIGN",
  seal: "FIELD_TYPE_SEAL",
  address: "FIELD_TYPE_ADDRESS",
};

const toBackendFields = (fields: ContractField[]): ContractFieldDto[] =>
  fields.map((f, idx) => ({
    cdFieldType: FE_TO_BE_FIELD[f.type],
    fieldKey: f.key,
    fieldLabel: f.label || f.key,
    fieldOrder: idx,
    autoFill: !!f.auto,
    required: !!f.required,
    description: f.description,
    defaultValue: f.defaultValue,
    pageNo: f.pageNo ?? null,
    posX: f.x != null ? String(f.x) : null,
    posY: f.y != null ? String(f.y) : null,
    posWidth: f.width != null ? String(f.width) : null,
    posHeight: f.height != null ? String(f.height) : null,
  })) as unknown as ContractFieldDto[];

/** AI 자동 변환 시 사용되는 크레딧 (관리자 서비스 금액에서 조정 가능). */
const AI_IMPORT_CREDIT_COST = 1;

type Stage = "select" | "converting" | "saving" | "error";

/** AI 변환 진행률 애니메이션 스텝. */
const AI_PROGRESS_STAGES = [
  { pct: 8, label: "📤 파일을 서버에 올리는 중..." },
  { pct: 25, label: "🔍 AI 가 문서를 분석하는 중..." },
  { pct: 50, label: "📊 표와 이미지 구조를 인식하는 중..." },
  { pct: 72, label: "🏷️ 변수 자리를 자동 감지하는 중..." },
  { pct: 88, label: "✍️ 서명·도장 위치를 찾는 중..." },
  { pct: 95, label: "🎨 계약서 양식으로 정리하는 중..." },
];

/** 터치 우선 디바이스 감지. */
const detectIsMobile = (): boolean => {
  if (typeof window === "undefined") return false;
  if (typeof window.matchMedia === "function") {
    if (window.matchMedia("(pointer: coarse)").matches) return true;
  }
  return /android|webos|iphone|ipad|ipod|opera mini|iemobile|mobile/i.test(
    navigator.userAgent || "",
  );
};

function RouteComponent() {
  const { history } = useRouter();
  const [file, setFile] = useState<File | null>(null);
  const [stage, setStage] = useState<Stage>("select");
  const [isDragOver, setIsDragOver] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string>("");
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [isMobile] = useState<boolean>(() => detectIsMobile());

  const uploadSource = useUploadImportSource();
  const createTemplate = useCreateContractTemplate();

  /* ── 라이브 변환 상태 (SSE 스트리밍) ────────────────────
   * outline 수신 → 빈 A4 골격 페이지 즉시 렌더
   * page 수신    → 해당 페이지 실 콘텐츠로 교체 (편집기 remount)
   * fields 수신  → 필드 배치 정보 보관
   * complete     → createTemplate 저장 → 이동                */
  const [livePages, setLivePages] = useState<(any | null)[]>([]);
  const [liveStatusMsg, setLiveStatusMsg] = useState("");
  const [liveDocKey, setLiveDocKey] = useState(0);
  const liveNameRef = useRef<string>("");

  /** 골격(스켈레톤) 페이지 — 콘텐츠 도착 전 표시할 빈 A4. */
  const skeletonPage = (pageNumber: number) => ({
    type: "contractPage",
    attrs: { pageNumber, width: 794, height: 1123 },
    content: [
      {
        type: "paragraph",
        attrs: { textAlign: "center" },
        content: [
          { type: "text", text: `${pageNumber}페이지 — AI 변환 중...` },
        ],
      },
    ],
  });

  const liveDoc =
    livePages.length > 0
      ? {
          type: "doc",
          content: livePages.map((p, i) => p ?? skeletonPage(i + 1)),
        }
      : null;
  const liveReceivedCount = livePages.filter(Boolean).length;

  // 진행률
  const [pct, setPct] = useState(0);
  const [progressMsg, setProgressMsg] = useState<string>("");
  const [elapsedSec, setElapsedSec] = useState(0);
  const timerRef = useRef<{ tick: any; sec: any } | null>(null);

  const startProgress = () => {
    setPct(0);
    setElapsedSec(0);
    setProgressMsg(AI_PROGRESS_STAGES[0].label);
    const t0 = Date.now();
    const tick = setInterval(() => {
      const elapsed = (Date.now() - t0) / 1000;
      const p = Math.min(95, 100 - 100 / Math.pow(1 + elapsed / 5, 1.4));
      setPct(Math.round(p));
      const stg = [...AI_PROGRESS_STAGES].reverse().find((s) => p >= s.pct);
      if (stg) setProgressMsg(stg.label);
    }, 200);
    const sec = setInterval(() => setElapsedSec((s) => s + 1), 1000);
    timerRef.current = { tick, sec };
  };
  const stopProgress = (final: "success" | "error") => {
    if (timerRef.current) {
      clearInterval(timerRef.current.tick);
      clearInterval(timerRef.current.sec);
      timerRef.current = null;
    }
    if (final === "success") {
      setPct(100);
      setProgressMsg("✅ 완료! 편집기로 이동합니다...");
    } else {
      setProgressMsg("❌ 오류가 발생했어요");
    }
  };
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current.tick);
        clearInterval(timerRef.current.sec);
      }
    };
  }, []);

  const handleFileChange = (f: File | null) => {
    if (!f) return;
    const ext = f.name.split(".").pop()?.toLowerCase();
    if (ext !== "pdf" && ext !== "docx" && ext !== "hwpx" && ext !== "hwp") {
      alert("AI 자동 변환은 PDF · 워드(DOCX) · 한글(HWP·HWPX) 만 지원해요.");
      return;
    }
    if (f.size > 20 * 1024 * 1024) {
      alert("파일이 너무 커요. 20MB 이하로 올려주세요.");
      return;
    }
    setFile(f);
    setStage("select");
    setErrorMsg("");
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
    const f = e.dataTransfer.files[0];
    if (f) handleFileChange(f);
  };

  const startAiConversion = async () => {
    if (!file) return;

    // 0) 크레딧 잔액 사전 확인 — 부족하면 충전 페이지로 안내
    try {
      const base = import.meta.env.VITE_API_BASE_URL ?? "";
      const token = localStorage.getItem("accessToken") ?? "";
      const walletRes = await fetch(`${base}/business/credit/wallet`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const walletJson = await walletRes.json();
      const balance = Number(walletJson?.data?.balance ?? 0);
      if (balance < AI_IMPORT_CREDIT_COST) {
        const goCharge = confirm(
          `크레딧이 부족해요. (현재 ${balance} 크레딧 · 필요 ${AI_IMPORT_CREDIT_COST} 크레딧)\n충전 페이지로 이동할까요?`,
        );
        if (goCharge) {
          history.push("/business/layout/user-credit-wallet-charge" as any);
        }
        return;
      }
    } catch {
      // 잔액 조회 실패는 변환 진행을 막지 않음 (BE 에서 최종 검증)
    }

    if (
      !confirm(
        `AI 자동 변환은 ${AI_IMPORT_CREDIT_COST} 크레딧이 사용돼요.\n계속할까요?`,
      )
    )
      return;

    try {
      setStage("converting");
      startProgress();
      setLivePages([]);
      setLiveStatusMsg("📤 파일을 올리는 중...");

      // 1) 원본 파일 업로드 (감사·재현용)
      const uploaded = await uploadSource.mutateAsync(file);

      // 2) AI 스트리밍 변환 — 페이지가 완성될 때마다 즉시 화면에 렌더
      const streamResult = await runStreamConversion(file, {
        onStatus: (msg) => setLiveStatusMsg(msg),
        onOutline: (pageCount, name) => {
          liveNameRef.current = name;
          setLivePages(Array(pageCount).fill(null));
          setLiveDocKey((k) => k + 1);
          setLiveStatusMsg(`📄 ${pageCount}페이지 문서 — 변환 시작`);
        },
        onPage: (pageNo, pageJson) => {
          setLivePages((prev) => {
            const next = [...prev];
            next[pageNo - 1] = pageJson;
            return next;
          });
          setLiveDocKey((k) => k + 1);
        },
      });
      stopProgress("success");

      // 3) 필드 변환
      let aiFields: ContractField[] = [];
      try {
        const raw: any[] = JSON.parse(streamResult.fieldsJson || "[]");
        aiFields = raw.map((f, i) => ({
          id: `ai-${i}`,
          type: f.type as FieldType,
          key: f.key || `${f.type}_${i}`,
          label: f.label || f.key || f.type,
          pageNo: Number(f.pageNo) || 1,
          x: Number(f.x) || 0,
          y: Number(f.y) || 0,
          width: Number(f.width) || 100,
          height: Number(f.height) || 40,
          auto: !!f.auto, // BE 가 자동 배치했음을 표시 → FE 가 DOM 실측치로 재조정
          required: false,
        }));
      } catch {
        aiFields = [];
      }

      const suggestedName =
        streamResult.suggestedName?.trim() ||
        liveNameRef.current ||
        file.name.replace(/\.(pdf|docx)$/i, "");

      // 4) 즉시 저장 — cdEditStep=REVIEW 로 검토 단계까지 완료
      setStage("saving");
      setLiveStatusMsg("💾 계약서 양식으로 저장하는 중...");
      const docJson = JSON.parse(streamResult.docJson);
      const bodyText = flattenTextFromDoc(docJson);
      const newSeq = await createTemplate.mutateAsync({
        contractName: suggestedName,
        contractContents: bodyText,
        contractDocJson: streamResult.docJson,
        cdContractStatus: "CONTRACT_STATUS_DRAFT",
        cdEditStep: "CONTRACT_EDIT_REVIEW",
        fields: toBackendFields(aiFields),
        sourceFileSeq: uploaded.fileSeq,
      });

      // 5) 완성된 계약서 편집기로 바로 이동 (검토 화면)
      if (newSeq) {
        history.replace(
          `/business/layout/contract-template-edit?seq=${newSeq}` as any,
        );
      } else {
        history.replace("/business/layout/contract-templates" as any);
      }
    } catch (err) {
      stopProgress("error");
      const msg = err instanceof Error ? err.message : String(err);
      // BE 잔액 부족 에러 → 충전 페이지 안내
      if (/크레딧.*부족|부족.*크레딧/.test(msg)) {
        if (confirm(`${msg}\n충전 페이지로 이동할까요?`)) {
          history.push("/business/layout/user-credit-wallet-charge" as any);
          return;
        }
      }
      setErrorMsg(msg);
      setStage("error");
    }
  };

  return (
    <>
      <LayoutHeader title={"AI 계약서 변환"} hasBackBtn hasFootPrint={false} />
      <ContractPinGate ownerOnly>
        <div className="flex-1 min-h-0 overflow-y-auto bg-[#F5F5F5]">
          {/* ── 파일 선택 ── */}
          {stage === "select" && (
            <>
              {/* AI 안내 카드 */}
              <div className="mx-4 mt-4 bg-gradient-to-br from-[#FFF7E6] to-[#FFFFFF] rounded-xl border border-[#FFE0B3] p-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-[24px]">✨</span>
                  <div className="text-[15px] font-bold text-[#111]">
                    AI 자동 변환
                  </div>
                </div>
                <div className="text-[12px] text-[#666] leading-relaxed">
                  PDF/DOCX 계약서를 올리면 AI 가 자동으로:
                </div>
                <ul className="mt-2 space-y-1 text-[12px] text-[#333]">
                  <li>• 변수(이름·주소·연락처 등) 자리 감지 → 자동 매핑</li>
                  <li>• 서명·도장·체크박스 위치 자동 배치</li>
                  <li>• 표·자간·페이지 레이아웃 그대로 보존</li>
                  <li>• 검토 화면까지 자동 완성 → 확인만 하면 완료</li>
                </ul>
                <div className="mt-3 pt-3 border-t border-[#FFE0B3] flex items-center justify-between">
                  <span className="text-[11px] text-[#666]">1회 사용 시</span>
                  <span className="text-[13px] font-bold text-[#FF9934]">
                    {AI_IMPORT_CREDIT_COST} 크레딧
                  </span>
                </div>
              </div>

              {/* 파일 선택 영역 */}
              <div
                {...(!isMobile && {
                  onDragOver: (e: React.DragEvent<HTMLDivElement>) => {
                    e.preventDefault();
                    setIsDragOver(true);
                  },
                  onDragLeave: () => setIsDragOver(false),
                  onDrop: handleDrop,
                })}
                onClick={() => fileInputRef.current?.click()}
                className={`mx-4 mt-4 rounded-xl border-2 border-dashed p-8 text-center cursor-pointer transition-colors ${
                  isDragOver
                    ? "border-[#FF9934] bg-[#FFF7E6]"
                    : "border-[#E9E9E9] bg-white"
                } ${isMobile ? "active:bg-[#FFF7E6]" : ""}`}
              >
                <div className="flex justify-center mb-2 text-[#999]">
                  <IconUpload className="w-10 h-10" />
                </div>
                <div className="text-[14px] font-semibold text-[#111] mb-1">
                  {file
                    ? file.name
                    : isMobile
                      ? "탭해서 파일을 선택해 주세요"
                      : "여기에 파일을 끌어다 놓으세요"}
                </div>
                {file && (
                  <div className="text-[12px] text-[#999]">
                    {(file.size / 1024).toFixed(1)} KB ·{" "}
                    {file.name.toLowerCase().endsWith(".pdf")
                      ? "PDF"
                      : file.name.toLowerCase().endsWith(".hwpx")
                        ? "한글(HWPX)"
                        : file.name.toLowerCase().endsWith(".hwp")
                          ? "한글(HWP)"
                          : "워드(DOCX)"}{" "}
                    파일
                  </div>
                )}
                {!file && (
                  <div className="mt-3 text-[10px] text-[#BDBDBD]">
                    PDF · DOCX · HWP · HWPX · 최대 20MB
                  </div>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.docx,.hwpx,.hwp"
                  hidden
                  onChange={(e) =>
                    handleFileChange(e.target.files?.[0] ?? null)
                  }
                />
              </div>

              {/* 변환 시작 버튼 */}
              <div className="px-4 mt-4 mb-6">
                <button
                  type="button"
                  onClick={startAiConversion}
                  disabled={!file}
                  className="w-full py-3 text-[14px] font-bold bg-[#FF9934] text-white rounded-full disabled:bg-[#E9E9E9] disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
                >
                  ✨ AI 로 자동 변환 ({AI_IMPORT_CREDIT_COST}크레딧)
                </button>
                <div className="text-[10px] text-[#999] text-center mt-2">
                  변환이 완료되면 검토 화면으로 자동 이동해요
                </div>
              </div>
            </>
          )}

          {/* ── 진행 중 — 라이브 변환 뷰 (문서가 눈앞에서 만들어짐) ── */}
          {(stage === "converting" || stage === "saving") && (
            <div className="flex flex-col h-full min-h-0">
              {/* 상단 상태 바 */}
              <div className="mx-4 mt-3 mb-2 bg-white rounded-xl border border-[#E9E9E9] px-4 py-3 overflow-hidden">
                {/* 상태 텍스트 — 항상 전체 폭 한 줄(truncate). 칩과 가로 공간을 다투지 않게 분리. */}
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-[18px] animate-pulse shrink-0">✨</span>
                  <div className="min-w-0 flex-1">
                    <div className="text-[13px] font-semibold text-[#111] truncate">
                      {liveStatusMsg ||
                        progressMsg ||
                        "AI 가 문서를 분석하는 중..."}
                    </div>
                    <div className="text-[10px] text-[#999] truncate">
                      {file?.name} · ⏱ {elapsedSec}초
                      {livePages.length > 0 && (
                        <>
                          {" "}
                          · 페이지 {liveReceivedCount}/{livePages.length}
                        </>
                      )}
                    </div>
                  </div>
                </div>
                {/* 페이지 칩 — 완료 페이지는 주황. 아래 줄에 줄바꿈으로 배치(가로 짜부라짐 방지).
                    페이지가 아주 많으면(>24) 칩 대신 진행선·N/M 텍스트만 표시. */}
                {livePages.length > 0 && livePages.length <= 24 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {livePages.map((p, i) => (
                      <span
                        key={i}
                        className={`w-6 h-6 rounded-full text-[10px] font-bold inline-flex items-center justify-center transition-colors shrink-0 ${
                          p
                            ? "bg-[#FF9934] text-white"
                            : "bg-[#F3F3F3] text-[#BBB] animate-pulse"
                        }`}
                      >
                        {i + 1}
                      </span>
                    ))}
                  </div>
                )}
                {/* 얇은 진행선 */}
                <div className="h-1 bg-[#F3F3F3] rounded-full overflow-hidden mt-2">
                  <div
                    className="h-full bg-gradient-to-r from-[#FFB763] to-[#FF9934] rounded-full transition-all duration-500"
                    style={{
                      width:
                        livePages.length > 0
                          ? `${Math.round(((liveReceivedCount + (stage === "saving" ? 1 : 0)) / (livePages.length + 1)) * 100)}%`
                          : `${pct}%`,
                    }}
                  />
                </div>
              </div>

              {/* 라이브 문서 — outline 수신 즉시 A4 골격 표시, 페이지 도착마다 실 콘텐츠 교체 */}
              {liveDoc ? (
                <div className="flex flex-1 min-h-0 px-4 pb-4">
                  <ContractRichEditor
                    key={`live-${liveDocKey}`}
                    initialJson={liveDoc}
                    readOnly
                  />
                </div>
              ) : (
                <div className="mx-4 bg-white rounded-xl border border-[#E9E9E9] p-8 text-center">
                  <div className="text-[28px] mb-2 animate-bounce">📄</div>
                  <div className="text-[13px] text-[#666]">
                    문서 구조를 분석하는 중...
                  </div>
                  <div className="text-[10px] text-[#999] mt-1">
                    잠시만요, 곧 문서가 나타나요
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── 오류 ── */}
          {stage === "error" && (
            <div className="mx-4 mt-4 bg-white rounded-xl border border-[#F87171] p-5">
              <div className="text-[14px] font-bold text-[#DC2626] mb-2">
                ❌ 변환 중 오류가 발생했어요
              </div>
              <div className="text-[12px] text-[#666] whitespace-pre-wrap mb-4">
                {errorMsg || "알 수 없는 오류"}
              </div>
              <div className="text-[11px] text-[#999] mb-3">
                • 크레딧 잔액이 충분한지 확인해 주세요
                <br />
                • PDF/DOCX 가 20MB 이하인지 확인해 주세요
                <br />• 잠시 후 다시 시도하거나 관리자에게 문의해 주세요
              </div>
              <button
                type="button"
                onClick={() => {
                  setStage("select");
                  setFile(null);
                  setErrorMsg("");
                }}
                className="w-full py-2.5 text-[13px] font-semibold border border-[#E9E9E9] text-[#666] rounded-full"
              >
                다시 시도하기
              </button>
            </div>
          )}
        </div>
      </ContractPinGate>
    </>
  );
}

/* ─────────────────────────────────────────────────────────────
 * SSE 스트리밍 소비 — POST + ReadableStream (EventSource 는 POST 불가)
 * 이벤트: status → outline → page×N → fields → complete | error
 * ──────────────────────────────────────────────────────────── */
interface StreamCallbacks {
  onStatus: (message: string) => void;
  onOutline: (pageCount: number, suggestedName: string) => void;
  onPage: (pageNo: number, pageJson: any) => void;
}
interface StreamResult {
  docJson: string;
  fieldsJson: string;
  suggestedName: string;
  pageCount: number;
  fieldCount: number;
  callSeq: number;
}

async function runStreamConversion(
  file: File,
  cb: StreamCallbacks,
): Promise<StreamResult> {
  const base = import.meta.env.VITE_API_BASE_URL ?? "";
  const token = localStorage.getItem("accessToken") ?? "";
  const form = new FormData();
  form.append("file", file);

  // VITE_API_BASE_URL 에 이미 /api 포함 — 중복 방지
  const res = await fetch(
    `${base}/business/contract/template/import/ai/stream`,
    {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body: form,
    },
  );
  if (!res.ok || !res.body) {
    throw new Error(`AI 변환 서버 연결 실패 (HTTP ${res.status})`);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder("utf-8");
  let buffer = "";
  let complete: StreamResult | null = null;
  let errorMsg: string | null = null;

  const handleEvent = (name: string, dataRaw: string) => {
    let data: any = {};
    try {
      data = JSON.parse(dataRaw);
    } catch {
      /* 무시 */
    }
    switch (name) {
      case "status":
        cb.onStatus(String(data.message ?? ""));
        break;
      case "outline":
        cb.onOutline(
          Number(data.pageCount) || 1,
          String(data.suggestedName ?? ""),
        );
        break;
      case "page":
        cb.onPage(Number(data.pageNo) || 1, data.pageJson);
        break;
      case "fields":
        // complete 이벤트에 fieldsJson 이 다시 오므로 여기선 상태 안내만
        cb.onStatus("✍️ 서명·도장·체크박스 배치 완료");
        break;
      case "complete":
        complete = {
          docJson: String(data.docJson ?? "{}"),
          fieldsJson: String(data.fieldsJson ?? "[]"),
          suggestedName: String(data.suggestedName ?? ""),
          pageCount: Number(data.pageCount) || 1,
          fieldCount: Number(data.fieldCount) || 0,
          callSeq: Number(data.callSeq) || 0,
        };
        break;
      case "error":
        errorMsg = String(data.message ?? "AI 변환 실패");
        break;
    }
  };

  // SSE 파서 — "event: X\ndata: {...}\n\n" 블록 단위
  const processBuffer = () => {
    let idx: number;
    while ((idx = buffer.indexOf("\n\n")) >= 0) {
      const block = buffer.slice(0, idx);
      buffer = buffer.slice(idx + 2);
      let event = "message";
      const dataLines: string[] = [];
      for (const line of block.split("\n")) {
        if (line.startsWith("event:")) event = line.slice(6).trim();
        else if (line.startsWith("data:")) dataLines.push(line.slice(5).trim());
      }
      if (dataLines.length) handleEvent(event, dataLines.join("\n"));
    }
  };

  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      processBuffer();
      if (errorMsg) break;
    }
  } catch (readErr) {
    // 서버가 chunked 종료 마커 없이 연결을 닫는 케이스 (Tomcat SSE) —
    // complete 이벤트를 이미 받았다면 데이터는 온전하므로 성공 처리.
    processBuffer();
    if (!complete && !errorMsg) throw readErr;
  }
  processBuffer();

  if (errorMsg) throw new Error(errorMsg);
  if (!complete)
    throw new Error("AI 변환이 완료되지 않았어요. 다시 시도해 주세요.");
  return complete;
}

/** docJson → 텍스트 평문 (본문 저장·검색용). */
const flattenTextFromDoc = (node: any): string => {
  if (!node) return "";
  if (typeof node.text === "string") return node.text;
  const children = Array.isArray(node.content) ? node.content : [];
  return children
    .map(flattenTextFromDoc)
    .join(node.type === "paragraph" ? "\n" : "");
};
